import moment from "moment"
import { isEmpty } from "utils/commonUtils"
import { dateTimeFormat } from "utils/dateFormatUtils"
import { MAJOR_SHORTAGE_COLOR, MINOR_SHORTAGE_COLOR } from "./InventoryWarningModal"
import { Colors } from "assets/scss/colors"

export const PRODUCTION_SCHEDULE_DATE_FORMAT = `dddd ${dateTimeFormat.americanFormat}`
export const OVERRIDE_DATE_FORMAT = "YYYY-MM-DD"
export const DAY_FORMAT = "dddd"

export const SLOT_TYPE = {
   PREMIUM: "PREMIUM",
   REGULAR: "REGULAR",
}

// Helper function to find valid configuration for a given date
export const findValidConfig = (configList, date) => {
   if (!configList || configList.length === 0) return {}

   return (
      configList.find(cfg => {
         if (!date) return true // If no date provided, use first config
         const currentDate = moment(date).startOf("day")
         const validFrom = moment(cfg.validFrom).startOf("day")
         const validTo = cfg.validTo ? moment(cfg.validTo).startOf("day") : null
         return (
            currentDate.isSameOrAfter(validFrom) &&
            (validTo === null || currentDate.isSameOrBefore(validTo))
         )
      }) || {}
   )
}

export const generateScheduleDateRange = ({
   startDate,
   endDate,
   timeLineData,
   dailyConfigurationsMap,
   overridesHashmap,
}) => {
   if (!startDate) return []

   const DEFAULT_DAYS_TO_SHOW = 7
   const startOfDay = moment(startDate).startOf("day")

   const calculateTotalDays = () => {
      if (endDate) {
         const endOfDay = moment(endDate).startOf("day")
         return Math.max(1, endOfDay.diff(startOfDay, "days") + 1)
      }

      if (isEmpty(timeLineData)) {
         return DEFAULT_DAYS_TO_SHOW
      }

      const latestDateInTimeline = timeLineData.reduce((latest, item) => {
         const current = moment(item?.date)
         return current.isAfter(latest) ? current : latest
      }, startOfDay.clone())

      return Math.max(1, latestDateInTimeline.diff(startOfDay, "days") + 1)
   }

   const totalDays = calculateTotalDays()

   return Array.from({ length: totalDays }, (_, i) => {
      const currentDate = startOfDay.clone().add(i, "day")
      const formattedDate = currentDate.format(PRODUCTION_SCHEDULE_DATE_FORMAT)
      const overrideKey = currentDate.format(OVERRIDE_DATE_FORMAT)
      const dayKey = currentDate.format(DAY_FORMAT)

      if (overridesHashmap?.has(overrideKey)) {
         const override = overridesHashmap.get(overrideKey)
         return {
            date: formattedDate,
            totalSlots: override.noOfSlots || 0,
            premiumSlots: override?.premiumSlots || 0,
            regularSlots: override?.regularSlots || 0,
         }
      }
      const configList = dailyConfigurationsMap.get(dayKey) || []
      const config = findValidConfig(configList, currentDate)

      return {
         date: formattedDate,
         totalSlots: config.totalSlots || 0,
         premiumSlots: config.premiumSlots || 0,
         regularSlots: config.regularSlots || 0,
      }
   })
}

export const createOverridesHashmap = overrides => {
   const overridesMap = new Map()

   if (!Array.isArray(overrides)) return overridesMap

   overrides.forEach(override => {
      const dateFrom = moment(override.dateFrom)
      const dateTo = moment(override.dateTo)

      const currentDate = moment(dateFrom)
      while (currentDate.isSameOrBefore(dateTo, "day")) {
         const dateKey = currentDate.format(OVERRIDE_DATE_FORMAT)
         overridesMap.set(dateKey, override)
         currentDate.add(1, "day")
      }
   })

   return overridesMap
}
export const createStageHashmap = stagesData => {
   const stageSeqMap = new Map()

   stagesData?.forEach(stage => {
      stageSeqMap.set(stage.id, stage.sequenceNo)
   })

   return stageSeqMap
}
export const createTimeLineHashmap = (timeLineData, stagesData, timeLineId) => {
   const timeLineMap = new Map()
   const stageSeqMap = new Map()

   stagesData?.forEach(stage => {
      stageSeqMap.set(stage.id, stage.sequenceNo)
   })

   if (isEmpty(timeLineData)) {
      const stageIds = stagesData?.map(stage => stage.id)
      if (!Array.isArray(stageIds)) return timeLineMap
      stageIds.forEach(stageId => {
         timeLineMap.set(stageId, [])
      })
   }

   timeLineData.forEach(item => {
      if (item.stageId) {
         const currentStageSequence = stageSeqMap.get(item.stageId)
         const clonedItem = { ...item }
         if (
            clonedItem?.timelineOrder?.notes &&
            Array.isArray(clonedItem?.timelineOrder?.notes)
         ) {
            clonedItem.timelineOrder.notes =
               clonedItem.timelineOrder.notes.filter(note => {
                  const noteStageSequence =
                     note.stage?.sequenceNo || stageSeqMap.get(note.stageId)

                  return noteStageSequence <= currentStageSequence
               })
         }

         if (!timeLineMap.has(clonedItem.stageId)) {
            timeLineMap.set(clonedItem.stageId, [])
         }
         if (clonedItem.timelineOrderId === timeLineId) {
            const newItem = { ...clonedItem, isSelected: true }
            timeLineMap.get(clonedItem.stageId).push(newItem)
         } else {
            timeLineMap.get(clonedItem.stageId).push(clonedItem)
         }
      }
   })
   return timeLineMap
}

export const createdailyConfigurationHashmap = dailyConfigurations => {
   const dailyConfigurationMap = new Map()

   if (!Array.isArray(dailyConfigurations)) return dailyConfigurationMap

   dailyConfigurations.forEach(item => {
      const key = item?.dailyDay
      if (!dailyConfigurationMap.has(key)) {
         dailyConfigurationMap.set(key, [])
      }

      dailyConfigurationMap.get(key).push(item)
   })

   // Sort by validFrom descending (latest config first)
   for (const [key, configs] of dailyConfigurationMap.entries()) {
      dailyConfigurationMap.set(
         key,
         configs.sort((a, b) => moment(b.validFrom).diff(moment(a.validFrom)))
      )
   }

   return dailyConfigurationMap
}

export const createWarningHashmap = (warnings = []) => {
   const warningsMap = new Map()

   warnings.forEach(({ cellId, stageId }) => {
      if (!warningsMap.has(cellId)) {
         warningsMap.set(cellId, [])
      }
      warningsMap.get(cellId).push({ stageId, cellId })
   })

   return warningsMap
}

export const getStageSequence = (stagesData = []) => {
   return new Map(stagesData.map(stage => [stage.id, stage.sequenceNo]))
}

export const checkDateForOverride = (date, overridesHashmap) => {
   const dateKey = moment(date).format(OVERRIDE_DATE_FORMAT)
   return overridesHashmap.has(dateKey)
}

export const getColumnWidth = slotCount => {
   if (slotCount === 1) return 3
   if (slotCount === 2) return 6
   if (slotCount === 3) return 9
   if (slotCount === 4) return 12
   return 12
}

export const getDynamicSlotWidth = totalSlots => {
   if (totalSlots <= 0) return 12
   return Math.floor(12 / totalSlots)
}

export const getSlotTypePercentage = (slotCount, totalSlots) => {
   if (totalSlots <= 0) return 0
   return (slotCount / totalSlots) * 100
}

export const getTotalSlotsForDay = (
   dayOfWeek,
   date,
   dailyConfigurationsMap,
   overridesHashmap,
   defaultSlots = 4
) => {
   const overrideKey = moment(date).format(OVERRIDE_DATE_FORMAT)
   if (overridesHashmap?.has(overrideKey)) {
      const override = overridesHashmap.get(overrideKey)
      return override?.noOfSlots || defaultSlots
   }

   const configList = dailyConfigurationsMap?.get(dayOfWeek)
   if (configList && configList.length > 0) {
      const config = findValidConfig(configList, date)
      return config.totalSlots || defaultSlots
   }
   return defaultSlots
}

export const sortStagesDataBySequenceNo = data => {
   return data?.sort((a, b) => a.sequenceNo - b.sequenceNo)
}

export const getInventoryWarningColor = (quantityRemaining, minorThreshold, mediumThreshold) => {
   if (quantityRemaining <= minorThreshold) return MAJOR_SHORTAGE_COLOR
   if (quantityRemaining <= mediumThreshold) return MINOR_SHORTAGE_COLOR
   return Colors.black
}